import { useState, useRef, useEffect } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { Navigate, useParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Grid, Box } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme, useTheme } from '@mui/material/styles';
import { mdiCog } from '@mdi/js';
import type { Value } from 'platejs';
import Typography from '@mui/material/Typography';

import { Icon } from 'common/components/Icon';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { BasePage } from 'hydra/pages/BasePage';
import { Loader } from 'common/components/Loader';
import { ArticleDocument, UpdateArticleDocument } from 'graphql/types';
import { ApolloError } from 'common/components/ApolloError';
import { Link } from 'common/components/Link';
import { PageIntroHeader } from 'common/components/PageIntroHeader';
import { ContentEditor } from 'common/components/ContentEditor';
import { ActionButton } from 'common/components/ActionButton';
import { Button } from 'common/components/Button';
import { LibraryArticleDrawer } from 'library/components/LibraryArticleDrawer';
import {
    goToArticleContentEditPage,
    goToArticleDetailPage,
    goToParentCollection,
} from 'library/utils/goTo';
import { deserializeContent } from 'common/components/ContentEditor/utils/deserialize';
import { useFrontendPermissions } from 'user/hooks';
import { PageTitle } from 'common/components/PageTitle';
import {
    ExitPageAlert,
    IExitPageAlertRef,
} from 'common/components/ExitPageAlert';
import { UnsavedChangesAlert } from 'common/components/Alerts';
import { isUUID } from 'common/utils/isUUID';
import { getUrl } from 'route/utils/getUrl';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { formatColor } from 'common/utils/color';
import { LibraryContextActionBar } from 'library/components/LibraryContextActionBar';

export const LibraryArticlePage = () => {
    const [translate] = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const [displaySnackbar] = useSnackbar();
    const theme = useTheme();
    const smallScreen = useMediaQuery((theme: Theme) =>
        theme.breakpoints.only('xs')
    );

    const [articleContent, setArticleContent] = useState<Value>();
    const [contentChanged, setContentChanged] = useState<boolean>(false);

    const { canUpdate } = useFrontendPermissions('libraryArticle');

    const [updateArticle, { loading: updateLoading, error: updateError }] =
        useMutation(UpdateArticleDocument);

    const articleDrawerOpen = !!useRouteMatch('LIBRARY_ARTICLE_EDIT');
    const editing = !!useRouteMatch('LIBRARY_ARTICLE_EDIT_CONTENT');

    const prevContent = useRef<Value>(undefined);
    const exitAlertRef = useRef<IExitPageAlertRef>(null);

    const [fetchArticle, { loading, error, data, called }] =
        useLazyQuery(ArticleDocument);

    useEffect(() => {
        const article = data?.libraryArticle;

        if (!article) return;

        // Set current article content
        const content = deserializeContent(article?.content);

        prevContent.current = content;
        setArticleContent(content);

        if (isUUID(id)) return;

        navigate(
            getUrl(
                editing ? 'LIBRARY_ARTICLE_EDIT_CONTENT' : 'LIBRARY_ARTICLE',
                { id: article.id }
            )
        );
    }, [data, editing, id, navigate]);

    useEffect(() => {
        const articleId = data?.libraryArticle?.id || {};

        if (loading || articleId === id || error) return;

        const articleQueryVariables = isUUID(id) ? { id } : { articleId: id };

        fetchArticle({ variables: articleQueryVariables });
    }, [data, fetchArticle, id, loading, error]);

    if (!id) return null;

    if (loading || error) {
        return (
            <BasePage>
                {error && <ApolloError error={error} />}
                {loading && <Loader />}
            </BasePage>
        );
    }

    const article = data?.libraryArticle;

    // When the response doesn't contain an article we redirect to the root of the library.
    if (called && !loading && !article) {
        return <Navigate to={getUrl('LIBRARY')} />;
    }

    const {
        collection: parent,
        title,
        headerImage,
        titleColor,
    } = article || {};

    let headerColor = formatColor(titleColor);

    if (!headerColor) {
        headerColor = headerImage ? 'common.white' : undefined;
    }

    const libraryTitle = translate('library');

    const articleSettingsButton = canUpdate && (
        <Button
            aria-label={translate('articleSettings')}
            component={Link}
            params={{ id }}
            size="small"
            startIcon={<Icon path={mdiCog} size="1.9rem" />}
            sx={
                smallScreen
                    ? { minWidth: 0, '.MuiButton-icon': { mr: -1 } }
                    : undefined
            }
            to="LIBRARY_ARTICLE_EDIT"
            variant="outlined"
        >
            {!smallScreen && translate('articleSettings')}
        </Button>
    );

    const contextActionBar = (
        <LibraryContextActionBar
            actions={articleSettingsButton}
            libraryTitle={libraryTitle}
            parent={parent}
        />
    );

    const handleSaveContent = async () => {
        const newContent = JSON.stringify(articleContent);

        await updateArticle({
            variables: {
                id,
                article: { content: newContent },
            },
        });

        displaySnackbar(translate('libraryArticle.contentSaveSuccess'), {
            variant: 'success',
        });

        // Set new version as prevVersion
        prevContent.current = articleContent;

        setContentChanged(false);
    };

    const handleCancelSaveContent = async () => {
        await setContentChanged(false);

        // Reset content
        await setArticleContent(
            prevContent.current
                ? structuredClone(prevContent.current)
                : undefined
        );

        setEditing(false);
    };

    const setEditing = (editing: boolean) => {
        if (!editing) {
            goToArticleDetailPage(navigate, id);

            return;
        }

        goToArticleContentEditPage(navigate, id);
    };

    const pageTitle = !!title ? `${libraryTitle} - ${title}` : undefined;

    const titleRow = (
        <Grid
            container
            spacing={3}
            sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <Grid>
                <Typography sx={{ color: headerColor }} variant="h1">
                    {title}
                </Typography>
            </Grid>
            {canUpdate && (
                <Grid component={Box} sx={{ display: 'flex' }}>
                    <ActionButton
                        outlined={!headerImage}
                        variant="extended"
                        onClick={() => {
                            setEditing(!editing);
                        }}
                    >
                        <>
                            {translate(editing ? 'closeEditor' : 'editArticle')}
                        </>
                    </ActionButton>
                </Grid>
            )}
        </Grid>
    );

    const header = headerImage ? (
        <PageIntroHeader image={headerImage}>{titleRow}</PageIntroHeader>
    ) : undefined;

    return (
        <BasePage contextActionBar={contextActionBar} header={header}>
            {updateError && <ApolloError error={updateError} />}
            {article && <PageTitle>{pageTitle || ''}</PageTitle>}
            <Box sx={{ py: 4 }}>
                {!header && <Box sx={{ mb: 3 }}>{titleRow}</Box>}
                <ContentEditor
                    content={articleContent}
                    readOnly={!editing}
                    toolbarProps={{
                        stickyOffset:
                            (theme.mixins.toolbar.height as number) +
                            (theme.mixins.contextActionBar.height as number),
                    }}
                    onChange={(content) => {
                        setArticleContent(content);
                        setContentChanged(true);
                    }}
                />
                {editing && (
                    <Button
                        color="primary"
                        disabled={!contentChanged}
                        loading={updateLoading}
                        variant="contained"
                        onClick={handleSaveContent}
                    >
                        {translate('save')}
                    </Button>
                )}
            </Box>
            {canUpdate && article && (
                <LibraryArticleDrawer
                    article={article}
                    open={articleDrawerOpen}
                    pageTitle={pageTitle}
                    onClose={() => goToArticleDetailPage(navigate, id)}
                    onDelete={() => {
                        goToParentCollection(navigate, parent);
                    }}
                    onUpdate={() => {
                        goToArticleDetailPage(navigate, id);
                    }}
                />
            )}
            <ExitPageAlert
                alert={UnsavedChangesAlert}
                ref={exitAlertRef}
                when={editing && contentChanged}
                onCancel={() => {
                    handleCancelSaveContent();

                    return true;
                }}
                onConfirm={async () => {
                    handleSaveContent();

                    return true;
                }}
            />
        </BasePage>
    );
};
